<template>
  <main class="events-page">
    <section class="events-hero">
      <div>
        <p class="eyebrow">Administración</p>
        <h1>Eventos</h1>
        <p class="hero-copy">Consulta eventos vigentes y finalizados, identifica sus relaciones y administra cada operación desde un solo lugar.</p>
      </div>
      <button type="button" class="primary-button hero-action" @click="showCreateModal = true">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        Crear evento
      </button>
    </section>

    <section class="summary-grid" aria-label="Resumen de eventos">
      <button type="button" class="summary-card total" :class="{ active: statusFilter === 'todos' }" @click="setStatus('todos')">
        <span class="summary-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg></span>
        <span><b>{{ summary.total }}</b><small>Total de eventos</small></span>
      </button>
      <button type="button" class="summary-card live" :class="{ active: statusFilter === 'en_curso' }" @click="setStatus('en_curso')">
        <span class="summary-icon"><span class="pulse-dot"></span></span>
        <span><b>{{ summary.en_curso }}</b><small>En curso</small></span>
      </button>
      <button type="button" class="summary-card upcoming" :class="{ active: statusFilter === 'proximos' }" @click="setStatus('proximos')">
        <span class="summary-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></span>
        <span><b>{{ summary.proximos }}</b><small>Próximos</small></span>
      </button>
      <button type="button" class="summary-card finished" :class="{ active: statusFilter === 'finalizados' }" @click="setStatus('finalizados')">
        <span class="summary-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4L19 6"/></svg></span>
        <span><b>{{ summary.finalizados }}</b><small>Finalizados</small></span>
      </button>
    </section>

    <section class="events-panel">
      <div class="panel-toolbar">
        <div class="filter-tabs" role="tablist" aria-label="Filtrar eventos por estado">
          <button
            v-for="tab in filterTabs"
            :key="tab.value"
            type="button"
            role="tab"
            :aria-selected="statusFilter === tab.value"
            :class="{ active: statusFilter === tab.value }"
            @click="setStatus(tab.value)"
          >
            {{ tab.label }}
            <span>{{ tab.count }}</span>
          </button>
        </div>

        <label class="search-box" for="event-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>
          <input
            id="event-search"
            v-model="searchDraft"
            type="search"
            autocomplete="off"
            placeholder="Buscar por nombre, código o evento principal"
            @keyup.enter="applySearch"
          />
          <button v-if="searchDraft" type="button" aria-label="Limpiar búsqueda" @click="clearSearch">×</button>
        </label>
      </div>

      <div v-if="error" class="state-message error-state" role="alert">
        <div>
          <b>No pudimos cargar los eventos</b>
          <p>{{ error }}</p>
        </div>
        <button type="button" class="secondary-button" @click="fetchEventos(pagination.page)">Reintentar</button>
      </div>

      <div v-else-if="loading" class="event-grid" aria-label="Cargando eventos">
        <article v-for="index in 6" :key="index" class="event-card skeleton-card">
          <div class="skeleton line short"></div>
          <div class="skeleton line title"></div>
          <div class="skeleton block"></div>
          <div class="skeleton line"></div>
          <div class="skeleton line"></div>
        </article>
      </div>

      <div v-else-if="eventos.length" class="event-grid">
        <article v-for="evento in eventos" :key="evento.id_evento" class="event-card" :class="`is-${evento.estado_temporal}`">
          <header class="event-card-header">
            <span class="status-pill" :class="evento.estado_temporal">
              <span v-if="evento.estado_temporal === 'en_curso'" class="status-dot"></span>
              {{ statusLabel(evento.estado_temporal) }}
            </span>
            <span v-if="evento.codigo" class="event-code">{{ evento.codigo }}</span>
          </header>

          <div class="event-title-wrap">
            <h2>{{ evento.nombre }}</h2>
            <span class="event-token">ID: {{ evento.token }}</span>
          </div>

          <div class="relationship-panel" :class="relationshipType(evento)">
            <span class="relationship-icon">
              <svg v-if="relationshipType(evento) === 'parent'" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="5" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M12 7.5v4M6 15.5v-2h12v2"/></svg>
              <svg v-else-if="relationshipType(evento) === 'child'" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4v5a4 4 0 0 0 4 4h10M15 9l4 4-4 4"/></svg>
              <svg v-else viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M8 9h8M8 13h5"/></svg>
            </span>
            <span>
              <b>{{ relationshipTitle(evento) }}</b>
              <small>{{ relationshipDescription(evento) }}</small>
            </span>
          </div>

          <dl class="event-dates">
            <div>
              <dt>Inicio</dt>
              <dd>{{ formatDate(evento.fecha_inicio) }}</dd>
            </div>
            <span class="date-line" aria-hidden="true"></span>
            <div>
              <dt>Fin</dt>
              <dd>{{ formatDate(evento.fecha_fin) }}</dd>
            </div>
          </dl>

          <div class="event-metrics">
            <span><b>{{ evento.cantidad_grupos }}</b> grupos</span>
            <span><b>{{ evento.cantidad_registros }}</b> registros</span>
            <span :class="{ controlled: !allowsDuplicates(evento) }">
              {{ allowsDuplicates(evento) ? 'Duplicados permitidos' : 'Duplicados controlados' }}
            </span>
          </div>

          <footer class="event-card-actions">
            <template v-if="evento.estado_temporal === 'finalizado'">
              <button type="button" class="primary-button compact" @click="openReactivate(evento)">
                Reactivar evento
              </button>
              <router-link class="secondary-button compact" :to="`/eventos/${evento.token}`">Editar</router-link>
            </template>
            <template v-else>
              <router-link class="primary-button compact" :to="`/eventos/${evento.token}`">Editar configuración</router-link>
              <router-link class="secondary-button compact" :to="`/lista-usuarios/${evento.token}`">Ver usuarios</router-link>
            </template>

            <details class="more-actions">
              <summary aria-label="Más acciones">•••</summary>
              <div class="more-actions-menu">
                <router-link v-if="evento.estado_temporal === 'finalizado'" :to="`/lista-usuarios/${evento.token}`">Ver usuarios</router-link>
                <router-link :to="{ path: '/auditoria-usuarios', query: { evento: evento.token } }">Ver auditoría</router-link>
                <button type="button" class="danger-action" @click="deleteEvento(evento)">Eliminar evento</button>
              </div>
            </details>
          </footer>
        </article>
      </div>

      <div v-else class="state-message empty-state">
        <span class="empty-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg></span>
        <h2>{{ searchDraft ? 'No encontramos coincidencias' : emptyTitle }}</h2>
        <p>{{ searchDraft ? 'Prueba con otro nombre, código o evento principal.' : emptyDescription }}</p>
        <button v-if="searchDraft" type="button" class="secondary-button" @click="clearSearch">Limpiar búsqueda</button>
      </div>

      <footer v-if="!loading && pagination.total" class="pagination-bar">
        <p>Mostrando {{ resultStart }}–{{ resultEnd }} de {{ pagination.total }} eventos</p>
        <div>
          <button type="button" :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">Anterior</button>
          <span>Página {{ pagination.page }} de {{ pagination.last_page }}</span>
          <button type="button" :disabled="pagination.page >= pagination.last_page" @click="changePage(pagination.page + 1)">Siguiente</button>
        </div>
      </footer>
    </section>

    <CreateEventoModal
      v-if="showCreateModal"
      :eventos="parentCandidates"
      @close="showCreateModal = false"
      @saved="handleEventCreated"
    />

    <div v-if="reactivationEvent" class="modal-backdrop" role="presentation" @click.self="closeReactivate">
      <section class="reactivation-modal" role="dialog" aria-modal="true" aria-labelledby="reactivation-title">
        <button type="button" class="modal-close" aria-label="Cerrar" @click="closeReactivate">×</button>
        <span class="modal-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 3-6.2M4 4v5h5"/></svg></span>
        <p class="eyebrow">Evento finalizado</p>
        <h2 id="reactivation-title">Reactivar {{ reactivationEvent.nombre }}</h2>
        <p>Define una nueva fecha de fin. La configuración, los grupos y los registros existentes se conservarán.</p>

        <label for="reactivation-end">Nueva fecha de fin</label>
        <input id="reactivation-end" v-model="reactivationEnd" type="datetime-local" :min="minimumEndDate" required />

        <div class="modal-actions">
          <button type="button" class="secondary-button" :disabled="reactivating" @click="closeReactivate">Cancelar</button>
          <button type="button" class="primary-button" :disabled="reactivating || !reactivationEnd" @click="reactivateEvent">
            {{ reactivating ? 'Reactivando…' : 'Confirmar reactivación' }}
          </button>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import CreateEventoModal from "@/components/modals/eventos/CreateEventoModal.vue";

export default {
  name: "EventosView",
  components: { CreateEventoModal },
  data() {
    return {
      eventos: [],
      parentCandidates: [],
      summary: { total: 0, vigentes: 0, en_curso: 0, proximos: 0, finalizados: 0 },
      pagination: { page: 1, per_page: 12, total: 0, last_page: 1 },
      statusFilter: "vigentes",
      searchDraft: "",
      appliedSearch: "",
      searchTimer: null,
      loading: false,
      error: "",
      showCreateModal: false,
      reactivationEvent: null,
      reactivationEnd: "",
      reactivating: false,
      toast: null,
    };
  },
  computed: {
    authPayload() {
      return { Authorization: this.$store.state.user?.token || "" };
    },
    filterTabs() {
      return [
        { value: "vigentes", label: "Vigentes", count: this.summary.vigentes },
        { value: "en_curso", label: "En curso", count: this.summary.en_curso },
        { value: "proximos", label: "Próximos", count: this.summary.proximos },
        { value: "finalizados", label: "Finalizados", count: this.summary.finalizados },
        { value: "todos", label: "Todos", count: this.summary.total },
      ];
    },
    resultStart() {
      return this.pagination.total ? ((this.pagination.page - 1) * this.pagination.per_page) + 1 : 0;
    },
    resultEnd() {
      return Math.min(this.pagination.page * this.pagination.per_page, this.pagination.total);
    },
    emptyTitle() {
      return this.statusFilter === "finalizados" ? "No hay eventos finalizados" : "No hay eventos en esta vista";
    },
    emptyDescription() {
      return this.statusFilter === "finalizados"
        ? "Cuando un evento termine aparecerá aquí y podrás consultarlo o reactivarlo."
        : "Cambia el filtro o crea un nuevo evento para comenzar.";
    },
    minimumEndDate() {
      return this.toInputDate(new Date());
    },
  },
  watch: {
    searchDraft() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.applySearch(), 350);
    },
  },
  created() {
    this.toast = useToast();
    const requestedStatus = String(this.$route.query.estado || "").toLowerCase();
    if (["vigentes", "en_curso", "proximos", "finalizados", "todos"].includes(requestedStatus)) {
      this.statusFilter = requestedStatus;
    }
    this.fetchEventos();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchEventos(page = 1) {
      this.loading = true;
      this.error = "";
      try {
        const response = await axios.post("/api/eventos/admin-list", {
          ...this.authPayload,
          estado: this.statusFilter,
          busqueda: this.appliedSearch,
          page,
          per_page: this.pagination.per_page,
        });
        const data = response?.data || {};
        this.eventos = Array.isArray(data.items) ? data.items : [];
        this.parentCandidates = Array.isArray(data.parent_candidates) ? data.parent_candidates : [];
        this.summary = { ...this.summary, ...(data.summary || {}) };
        this.pagination = { ...this.pagination, ...(data.pagination || {}) };

        if (!this.eventos.length && this.pagination.total > 0 && page > this.pagination.last_page) {
          await this.fetchEventos(this.pagination.last_page);
        }
      } catch (error) {
        this.eventos = [];
        this.error = this.errorMessage(error, "No fue posible consultar el listado administrativo.");
      } finally {
        this.loading = false;
      }
    },
    setStatus(status) {
      if (this.statusFilter === status && this.pagination.page === 1) return;
      this.statusFilter = status;
      this.$router.replace({ query: { ...this.$route.query, estado: status } }).catch(() => {});
      this.fetchEventos(1);
    },
    applySearch() {
      const next = String(this.searchDraft || "").trim();
      if (next === this.appliedSearch) return;
      this.appliedSearch = next;
      this.fetchEventos(1);
    },
    clearSearch() {
      window.clearTimeout(this.searchTimer);
      this.searchDraft = "";
      if (this.appliedSearch !== "") {
        this.appliedSearch = "";
        this.fetchEventos(1);
      }
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.last_page || page === this.pagination.page) return;
      this.fetchEventos(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleEventCreated() {
      this.showCreateModal = false;
      this.fetchEventos(1);
    },
    relationshipType(event) {
      if (Number(event.evento_padre_id || 0) > 0) return "child";
      if (Number(event.cantidad_subeventos || 0) > 0) return "parent";
      return "standalone";
    },
    relationshipTitle(event) {
      const type = this.relationshipType(event);
      if (type === "parent") return "Evento principal";
      if (type === "child") return "Subevento vinculado";
      return "Evento independiente";
    },
    relationshipDescription(event) {
      const type = this.relationshipType(event);
      if (type === "parent") {
        const count = Number(event.cantidad_subeventos || 0);
        return `${count} ${count === 1 ? "subevento vinculado" : "subeventos vinculados"}`;
      }
      if (type === "child") return `Principal: ${event.evento_padre_nombre || "Evento no disponible"}`;
      return "Sin eventos vinculados";
    },
    statusLabel(status) {
      return { en_curso: "En curso", proximo: "Próximo", finalizado: "Finalizado" }[status] || "Sin estado";
    },
    allowsDuplicates(event) {
      return String(event.permitir_duplicados ?? "0") === "1";
    },
    formatDate(value) {
      if (!value) return "Sin fecha";
      const normalized = String(value).replace(" ", "T");
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    toInputDate(date) {
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    openReactivate(event) {
      const defaultEnd = new Date();
      defaultEnd.setDate(defaultEnd.getDate() + 7);
      defaultEnd.setHours(23, 59, 0, 0);
      this.reactivationEvent = event;
      this.reactivationEnd = this.toInputDate(defaultEnd);
    },
    closeReactivate() {
      if (this.reactivating) return;
      this.reactivationEvent = null;
      this.reactivationEnd = "";
    },
    async reactivateEvent() {
      if (!this.reactivationEvent || !this.reactivationEnd) return;
      this.reactivating = true;
      try {
        const response = await axios.post(`/api/eventos/reactivar/${this.reactivationEvent.token}`, {
          ...this.authPayload,
          fecha_fin: this.reactivationEnd,
        });
        this.toast.success(response?.data?.message || "Evento reactivado correctamente.");
        this.closeReactivateAfterRequest();
        await this.fetchEventos(1);
      } catch (error) {
        this.toast.error(this.errorMessage(error, "No se pudo reactivar el evento."));
      } finally {
        this.reactivating = false;
      }
    },
    closeReactivateAfterRequest() {
      this.reactivationEvent = null;
      this.reactivationEnd = "";
    },
    async deleteEvento(event) {
      const isSecurity = !!this.$store?.state?.securityMode;

      if (!isSecurity) {
        const confirmed = window.confirm(`¿Eliminar el evento “${event.nombre}”? Esta acción no se puede deshacer.`);
        if (!confirmed) return;

        try {
          const response = await axios.post(`/api/eventos/delete/${event.token}`, this.authPayload);
          this.toast.success(response?.data?.message || "Evento eliminado.");
          await this.fetchEventos(this.pagination.page);
        } catch (error) {
          this.toast.error(this.errorMessage(error, "No se pudo eliminar el evento."));
        }
        return;
      }

      const typed = window.prompt(
        `SECURITY MODE ACTIVO\n\nSe descargará un respaldo XLSX y después se eliminarán el evento, sus grupos y sus datos relacionados.\n\nEscribe ELIMINAR para continuar:`
      );
      if (typed !== "ELIMINAR") {
        this.toast.info("Operación cancelada.");
        return;
      }

      try {
        const response = await fetch(`/api/security/eventos/purge/${event.token}`, { method: "POST" });
        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        if (contentType.includes("application/json")) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || "El servidor no devolvió el respaldo.");
        }
        if (!response.ok) throw new Error("No se pudo ejecutar el borrado protegido.");

        const blob = await response.blob();
        const disposition = response.headers.get("content-disposition") || "";
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
        let filename = match?.[1] ? decodeURIComponent(match[1]) : `backup_evento_${event.token}.xlsx`;
        if (!filename.toLowerCase().endsWith(".xlsx")) filename += ".xlsx";

        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(downloadUrl);
        this.toast.success("Respaldo descargado y evento eliminado.");
        await this.fetchEventos(this.pagination.page);
      } catch (error) {
        this.toast.error(error?.message || "No se pudo ejecutar el borrado protegido.");
      }
    },
    errorMessage(error, fallback) {
      const data = error?.response?.data;
      if (typeof data?.messages?.error === "string") return data.messages.error;
      if (typeof data?.message === "string") return data.message;
      if (typeof data?.error === "string") return data.error;
      return fallback;
    },
  },
};
</script>

<style scoped>
.events-page {
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  padding: 30px 28px 48px;
  color: #172033;
}

.events-hero {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  overflow: hidden;
  padding: 32px 36px;
  border: 1px solid #dce8f8;
  border-radius: 22px;
  background: linear-gradient(115deg, #f7fbff 0%, #eef6ff 56%, #f7f5ff 100%);
}

.events-hero::after {
  position: absolute;
  top: -80px;
  right: 12%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.07);
  content: "";
}

.eyebrow {
  margin: 0 0 6px;
  color: #2563eb;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.events-hero h1 {
  margin: 0;
  color: #111827;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.hero-copy {
  max-width: 700px;
  margin: 9px 0 0;
  color: #5c6b82;
  font-size: 15px;
  line-height: 1.6;
}

.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 17px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 750;
  line-height: 1;
  text-decoration: none;
  transition: transform 150ms ease, box-shadow 150ms ease, background 150ms ease;
}

.primary-button {
  background: #2563eb;
  color: white;
  box-shadow: 0 7px 18px rgba(37, 99, 235, 0.2);
}

.primary-button:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.primary-button:disabled,
.secondary-button:disabled { cursor: not-allowed; opacity: 0.55; }

.secondary-button {
  border-color: #d9e1ec;
  background: white;
  color: #334155;
}

.secondary-button:hover:not(:disabled) { border-color: #93b4e8; background: #f8fbff; }
.primary-button svg { width: 18px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 2; }
.hero-action { position: relative; z-index: 1; flex: 0 0 auto; }

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin: 18px 0;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 18px;
  border: 1px solid #e1e7ef;
  border-radius: 16px;
  background: white;
  color: #243044;
  text-align: left;
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.035);
  transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;
}

.summary-card:hover,
.summary-card.active { border-color: #a8c4ef; box-shadow: 0 8px 24px rgba(39, 78, 139, 0.09); transform: translateY(-1px); }
.summary-icon { display: grid; width: 42px; height: 42px; flex: 0 0 42px; place-items: center; border-radius: 12px; background: #eef4ff; color: #2563eb; }
.summary-icon svg { width: 21px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.8; }
.summary-card.live .summary-icon { background: #e9faf2; color: #16845b; }
.summary-card.upcoming .summary-icon { background: #fff6e6; color: #c8780b; }
.summary-card.finished .summary-icon { background: #f1f3f6; color: #667085; }
.summary-card > span:last-child { min-width: 0; }
.summary-card b { display: block; color: #172033; font-size: 24px; line-height: 1; }
.summary-card small { display: block; margin-top: 6px; color: #6b778c; font-size: 13px; font-weight: 650; }
.pulse-dot { width: 11px; height: 11px; border: 3px solid #bbefda; border-radius: 50%; background: #1d9a6c; box-sizing: content-box; }

.events-panel {
  overflow: visible;
  border: 1px solid #e1e7ef;
  border-radius: 20px;
  background: #f9fbfd;
  box-shadow: 0 12px 35px rgba(15, 23, 42, 0.055);
}

.panel-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4eaf1;
  border-radius: 20px 20px 0 0;
  background: white;
}

.filter-tabs { display: flex; gap: 5px; overflow-x: auto; padding: 2px; }
.filter-tabs button { display: inline-flex; align-items: center; gap: 7px; min-height: 38px; padding: 0 12px; border: 0; border-radius: 9px; background: transparent; color: #667085; font-size: 13px; font-weight: 700; white-space: nowrap; }
.filter-tabs button span { display: inline-grid; min-width: 21px; height: 21px; place-items: center; padding: 0 5px; border-radius: 999px; background: #eef1f5; color: #667085; font-size: 11px; }
.filter-tabs button:hover { background: #f4f7fb; color: #334155; }
.filter-tabs button.active { background: #eaf2ff; color: #1d4ed8; }
.filter-tabs button.active span { background: white; color: #1d4ed8; }

.search-box { display: flex; align-items: center; width: min(380px, 100%); min-height: 42px; padding: 0 12px; border: 1px solid #d8e0eb; border-radius: 11px; background: #fbfcfe; transition: border-color 150ms ease, box-shadow 150ms ease; }
.search-box:focus-within { border-color: #7aa7e8; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.09); }
.search-box svg { width: 19px; flex: 0 0 19px; fill: none; stroke: #8290a4; stroke-linecap: round; stroke-width: 1.8; }
.search-box input { width: 100%; min-width: 80px; padding: 0 9px; border: 0; outline: 0; background: transparent; color: #263247; font-size: 13px; }
.search-box input::placeholder { color: #98a2b3; }
.search-box button { border: 0; background: transparent; color: #7c8798; font-size: 21px; line-height: 1; }

.event-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; padding: 20px; }
.event-card { position: relative; display: flex; min-width: 0; min-height: 410px; flex-direction: column; padding: 20px; border: 1px solid #e1e7ef; border-radius: 16px; background: white; box-shadow: 0 4px 13px rgba(30, 41, 59, 0.04); transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease; }
.event-card:hover { border-color: #bfd1ea; box-shadow: 0 13px 30px rgba(31, 63, 109, 0.09); transform: translateY(-2px); }
.event-card.is-finalizado { background: linear-gradient(180deg, #ffffff 0%, #fbfbfc 100%); }
.event-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 25px; }
.status-pill { display: inline-flex; align-items: center; gap: 7px; padding: 5px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; letter-spacing: 0.02em; text-transform: uppercase; }
.status-pill.en_curso { background: #e8f8f0; color: #147a54; }
.status-pill.proximo { background: #fff4dd; color: #a95f00; }
.status-pill.finalizado { background: #eef0f3; color: #667085; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; background: #1c9b6c; box-shadow: 0 0 0 3px #c8eedf; }
.event-code { max-width: 45%; overflow: hidden; color: #8390a3; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.event-title-wrap { margin: 15px 0 14px; }
.event-title-wrap h2 { display: -webkit-box; overflow: hidden; min-height: 48px; margin: 0 0 5px; color: #182134; font-size: 19px; font-weight: 790; line-height: 1.28; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.event-token { color: #98a2b3; font-size: 11px; }

.relationship-panel { display: flex; align-items: center; gap: 11px; min-height: 62px; padding: 11px 12px; border: 1px solid #e4e9f0; border-radius: 12px; background: #f8fafc; }
.relationship-panel.parent { border-color: #cfe0fb; background: #f3f7ff; }
.relationship-panel.child { border-color: #ded6fb; background: #f7f5ff; }
.relationship-icon { display: grid; width: 34px; height: 34px; flex: 0 0 34px; place-items: center; border-radius: 9px; background: white; color: #64748b; box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08); }
.relationship-panel.parent .relationship-icon { color: #2563eb; }
.relationship-panel.child .relationship-icon { color: #7450c9; }
.relationship-icon svg { width: 19px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 1.8; }
.relationship-panel b { display: block; color: #344054; font-size: 12px; }
.relationship-panel small { display: block; overflow: hidden; margin-top: 3px; color: #7b8798; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }

.event-dates { display: grid; grid-template-columns: 1fr 18px 1fr; align-items: center; gap: 7px; margin: 17px 0 13px; }
.event-dates div { min-width: 0; }
.event-dates dt { margin-bottom: 4px; color: #98a2b3; font-size: 10px; font-weight: 800; letter-spacing: 0.07em; text-transform: uppercase; }
.event-dates dd { margin: 0; color: #3d495c; font-size: 12px; font-weight: 650; line-height: 1.4; }
.date-line { height: 1px; background: #d5dce6; }
.event-metrics { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 17px; }
.event-metrics span { padding: 5px 8px; border-radius: 7px; background: #f3f5f8; color: #667085; font-size: 10px; font-weight: 650; }
.event-metrics span.controlled { background: #eaf7f1; color: #26745a; }
.event-metrics b { color: inherit; }

.event-card-actions { display: flex; align-items: center; gap: 8px; margin-top: auto; padding-top: 15px; border-top: 1px solid #edf0f4; }
.compact { min-height: 37px; padding: 0 12px; font-size: 12px; }
.event-card-actions > .primary-button { flex: 1 1 auto; }
.event-card-actions > .secondary-button { flex: 0 0 auto; }
.more-actions { position: relative; flex: 0 0 auto; }
.more-actions summary { display: grid; width: 38px; height: 38px; cursor: pointer; list-style: none; place-items: center; border: 1px solid #d9e1ec; border-radius: 9px; background: white; color: #64748b; font-weight: 800; letter-spacing: 1px; }
.more-actions summary::-webkit-details-marker { display: none; }
.more-actions[open] summary { border-color: #8aafe7; background: #f4f8ff; }
.more-actions-menu { position: absolute; z-index: 8; right: 0; bottom: 44px; display: grid; width: 165px; overflow: hidden; padding: 6px; border: 1px solid #dce3ec; border-radius: 11px; background: white; box-shadow: 0 15px 35px rgba(15, 23, 42, 0.16); }
.more-actions-menu a,
.more-actions-menu button { width: 100%; padding: 9px 10px; border: 0; border-radius: 7px; background: transparent; color: #3c485c; font-size: 12px; font-weight: 650; text-align: left; text-decoration: none; }
.more-actions-menu a:hover,
.more-actions-menu button:hover { background: #f4f7fb; }
.more-actions-menu .danger-action { color: #c23b3b; }

.state-message { margin: 20px; border: 1px solid #dfe6ef; border-radius: 15px; background: white; }
.error-state { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 20px; border-color: #f4cccc; background: #fffafa; }
.error-state b { color: #9f2f2f; }
.error-state p { margin: 4px 0 0; color: #7e5555; font-size: 13px; }
.empty-state { display: grid; min-height: 330px; place-items: center; align-content: center; padding: 45px 20px; text-align: center; }
.empty-icon { display: grid; width: 56px; height: 56px; place-items: center; border-radius: 16px; background: #edf4ff; color: #477cce; }
.empty-icon svg { width: 27px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-width: 1.7; }
.empty-state h2 { margin: 15px 0 5px; font-size: 18px; }
.empty-state p { max-width: 440px; margin: 0 0 18px; color: #7a8798; font-size: 13px; }

.pagination-bar { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 15px 20px; border-top: 1px solid #e4eaf1; border-radius: 0 0 20px 20px; background: white; }
.pagination-bar p { margin: 0; color: #778396; font-size: 12px; }
.pagination-bar div { display: flex; align-items: center; gap: 10px; }
.pagination-bar button { min-height: 35px; padding: 0 12px; border: 1px solid #d8e0e9; border-radius: 8px; background: white; color: #405069; font-size: 12px; font-weight: 700; }
.pagination-bar button:disabled { cursor: not-allowed; opacity: 0.45; }
.pagination-bar span { color: #6d798a; font-size: 12px; }

.modal-backdrop { position: fixed; z-index: 1000; inset: 0; display: grid; padding: 20px; place-items: center; background: rgba(15, 23, 42, 0.58); backdrop-filter: blur(2px); }
.reactivation-modal { position: relative; width: min(470px, 100%); padding: 29px; border-radius: 18px; background: white; box-shadow: 0 25px 70px rgba(15, 23, 42, 0.25); }
.modal-close { position: absolute; top: 13px; right: 14px; width: 33px; height: 33px; border: 0; border-radius: 50%; background: #f2f4f7; color: #687386; font-size: 22px; }
.modal-icon { display: grid; width: 48px; height: 48px; margin-bottom: 18px; place-items: center; border-radius: 14px; background: #eaf2ff; color: #2563eb; }
.modal-icon svg { width: 24px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2; }
.reactivation-modal h2 { margin: 0 0 8px; color: #192235; font-size: 22px; }
.reactivation-modal > p:not(.eyebrow) { margin: 0 0 20px; color: #68758a; font-size: 13px; line-height: 1.55; }
.reactivation-modal label { display: block; margin-bottom: 7px; color: #374151; font-size: 12px; font-weight: 750; }
.reactivation-modal input { width: 100%; min-height: 44px; padding: 0 12px; border: 1px solid #cfd8e5; border-radius: 9px; outline: 0; color: #263247; }
.reactivation-modal input:focus { border-color: #6f9fe3; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.09); }
.modal-actions { display: flex; justify-content: flex-end; gap: 9px; margin-top: 24px; }

.skeleton-card { pointer-events: none; }
.skeleton { border-radius: 7px; background: linear-gradient(90deg, #eef1f5 25%, #f7f8fa 45%, #eef1f5 65%); background-size: 300% 100%; animation: shimmer 1.25s infinite; }
.skeleton.line { height: 14px; margin-bottom: 15px; }
.skeleton.line.short { width: 30%; height: 22px; }
.skeleton.line.title { width: 78%; height: 24px; margin-top: 8px; }
.skeleton.block { height: 62px; margin: 10px 0 22px; }
@keyframes shimmer { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }

@media (max-width: 1120px) {
  .event-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .panel-toolbar { align-items: stretch; flex-direction: column; }
  .search-box { width: 100%; }
}

@media (max-width: 760px) {
  .events-page { padding: 18px 12px 36px; }
  .events-hero { align-items: stretch; flex-direction: column; padding: 25px 22px; }
  .hero-action { align-self: flex-start; }
  .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .event-grid { grid-template-columns: 1fr; padding: 13px; }
  .panel-toolbar { padding: 14px; }
  .pagination-bar { align-items: stretch; flex-direction: column; }
  .pagination-bar div { justify-content: space-between; }
}

@media (max-width: 460px) {
  .summary-grid { grid-template-columns: 1fr; }
  .event-card-actions { align-items: stretch; flex-wrap: wrap; }
  .event-card-actions > .primary-button,
  .event-card-actions > .secondary-button { flex: 1 1 calc(50% - 24px); }
  .more-actions { margin-left: auto; }
  .event-dates { grid-template-columns: 1fr; }
  .date-line { display: none; }
  .modal-actions { align-items: stretch; flex-direction: column-reverse; }
}
</style>
